import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import AnimatedSection from '../../components/common/AnimatedSection';

// 样式组件
const RevenueContainer = styled('div', {
  padding: '0',
  maxWidth: '100%',
});

// 收益优势区域
const AdvantageSection = styled('div', {
  background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
  padding: '80px 0',
  
  '& h2': {
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '30px',
    textAlign: 'center',
    color: '#121b33',
  },
  
  '& p.section-intro': {
    fontSize: '18px',
    lineHeight: '1.6',
    color: '#666',
    maxWidth: '800px',
    margin: '0 auto 60px',
    textAlign: 'center',
  },
});

const AdvantageGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '30px',
  maxWidth: '1100px',
  margin: '0 auto',
  padding: '0 20px',
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const AdvantageCard = styled('div', {
  background: 'white',
  borderRadius: '12px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
  padding: '30px',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  transition: 'transform 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 15px 35px rgba(53, 99, 233, 0.1)',
  },
  
  '& .card-header': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    
    '& .icon': {
      width: '60px',
      height: '60px',
      background: 'rgba(53, 99, 233, 0.1)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '28px',
      color: '#3563E9',
      marginRight: '15px',
    },
    
    '& h3': {
      fontSize: '22px',
      fontWeight: '600',
      color: '#121b33',
      margin: 0,
    },
  },
  
  '& p': {
    fontSize: '16px',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '15px',
    flex: 1,
  },
  
  '& .metrics': {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '20px',
    borderTop: '1px solid #eaeaea',
    paddingTop: '20px',
    
    '& .metric': {
      textAlign: 'center',
      
      '& .value': {
        fontSize: '24px',
        fontWeight: '700',
        color: '#3563E9',
        marginBottom: '5px',
      },
      
      '& .label': {
        fontSize: '14px',
        color: '#666',
      },
    },
  },
});

// 收益模型区域
const ModelSection = styled('div', {
  padding: '80px 0',
  
  '& h2': {
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '50px',
    textAlign: 'center',
    color: '#121b33',
  },
});

const ModelCard = styled('div', {
  background: 'white',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
  maxWidth: '1000px',
  margin: '0 auto 60px',
  
  '& .card-header': {
    background: 'linear-gradient(135deg, #3563E9 0%, #6C5CE7 100%)',
    color: 'white',
    padding: '30px',
    
    '& h3': {
      fontSize: '26px',
      fontWeight: '600',
      margin: '0 0 15px',
    },
    
    '& p': {
      fontSize: '16px',
      color: 'rgba(255, 255, 255, 0.9)',
      margin: 0,
      lineHeight: '1.6',
    },
  },
  
  '& .card-body': {
    padding: '30px',
    
    '& p': {
      fontSize: '16px',
      color: '#666',
      lineHeight: '1.6',
      marginBottom: '20px',
    },
  },
});

const ModelFeatures = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '20px',
  margin: '30px 0',
});

const ModelFeature = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '15px',
  
  '& .icon': {
    minWidth: '40px',
    height: '40px',
    borderRadius: '8px',
    background: 'rgba(53, 99, 233, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: '#3563E9',
  },
  
  '& h4': {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#121b33',
  },
  
  '& p': {
    fontSize: '15px',
    color: '#666',
    lineHeight: '1.5',
    margin: 0,
  },
});

// 案例分析区域
const CaseStudySection = styled('div', {
  background: '#f8fafc',
  padding: '80px 0',
  
  '& h2': {
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '50px',
    textAlign: 'center',
    color: '#121b33',
  },
});

const CaseStudyCards = styled('div', {
  maxWidth: '1100px',
  margin: '0 auto',
  padding: '0 20px',
});

const CaseStudyCard = styled('div', {
  background: 'white',
  borderRadius: '12px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
  padding: '30px',
  marginBottom: '30px',
  
  '&:last-child': {
    marginBottom: 0,
  },
  
  '& h3': {
    fontSize: '22px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#121b33',
    display: 'flex',
    alignItems: 'center',
    
    '& .tag': {
      fontSize: '14px',
      fontWeight: '500',
      padding: '5px 10px',
      borderRadius: '4px',
      background: 'rgba(53, 99, 233, 0.1)',
      color: '#3563E9',
      marginLeft: '15px',
    },
  },
  
  '& p': {
    fontSize: '16px',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  
  '& .results': {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    background: '#f8fafc',
    padding: '20px',
    borderRadius: '8px',
    marginTop: '20px',
    
    '& .result': {
      '& .value': {
        fontSize: '24px',
        fontWeight: '700',
        color: '#3563E9',
        marginBottom: '5px',
      },
      
      '& .label': {
        fontSize: '14px',
        color: '#666',
      },
    },
  },
});

// AI收益页面组件
const AiRevenue: React.FC = () => {
  const { t } = useTranslation();
  
  // 收益优势数据
  const advantages = [
    {
      icon: "📈",
      title: t('ai.revenue.advantages.card1.title', '实时市场数据监测与预测'),
      description: t('ai.revenue.advantages.card1.description', '通过AI算法实时分析主流加密货币市场走势，预测最佳挖矿时机，让矿工能够在最有利的时段进行挖矿，显著提高收益。'),
      metrics: [
        { value: '37%', label: t('ai.revenue.advantages.card1.metric1', '预测准确率') },
        { value: '15%', label: t('ai.revenue.advantages.card1.metric2', '收益提升') },
      ],
    },
    {
      icon: "🔍",
      title: t('ai.revenue.advantages.card2.title', '智能算力动态分配'),
      description: t('ai.revenue.advantages.card2.description', '基于网络难度自动调整挖矿功率，在保持高效挖矿的同时降低能耗，实现收益与成本的最佳平衡。'),
      metrics: [
        { value: '25%', label: t('ai.revenue.advantages.card2.metric1', '能耗降低') },
        { value: '20%', label: t('ai.revenue.advantages.card2.metric2', '效率提升') },
      ],
    },
    {
      icon: "🔄",
      title: t('ai.revenue.advantages.card3.title', '收益风险平衡模型'),
      description: t('ai.revenue.advantages.card3.description', '建立多币种组合挖矿策略，分散市场风险，保持稳定的收益流，即使在市场波动时期也能获得可预期的回报。'),
      metrics: [
        { value: '40%', label: t('ai.revenue.advantages.card3.metric1', '风险降低') },
        { value: '28%', label: t('ai.revenue.advantages.card3.metric2', '稳定性提升') },
      ],
    },
    {
      icon: "🔧",
      title: t('ai.revenue.advantages.card4.title', '设备健康度智能诊断'),
      description: t('ai.revenue.advantages.card4.description', '通过实时监控矿机运行状态，预测潜在故障并提供维护建议，减少停机时间，延长设备寿命，提高整体挖矿效率。'),
      metrics: [
        { value: '99.9%', label: t('ai.revenue.advantages.card4.metric1', '运行稳定性') },
        { value: '45%', label: t('ai.revenue.advantages.card4.metric2', '故障减少') },
      ],
    },
  ];
  
  // 收益模型数据
  const revenueModels = [
    {
      title: t('ai.revenue.models.model1.title', '智能动态挖矿模型'),
      subtitle: t('ai.revenue.models.model1.subtitle', '基于市场数据和网络状态的实时优化挖矿策略'),
      description: t('ai.revenue.models.model1.description', '该模型结合市场价格走势、网络难度变化和能源成本波动等多维数据，通过深度学习算法生成动态挖矿决策，在最佳时机以最优参数进行挖矿操作，实现收益最大化。'),
      features: [
        {
          icon: "⏱️",
          title: t('ai.revenue.models.model1.feature1.title', '时间优化'),
          description: t('ai.revenue.models.model1.feature1.description', '自动选择最佳挖矿时段'),
        },
        {
          icon: "⚡",
          title: t('ai.revenue.models.model1.feature2.title', '功率优化'),
          description: t('ai.revenue.models.model1.feature2.description', '根据回报动态调整功率'),
        },
        {
          icon: "💰",
          title: t('ai.revenue.models.model1.feature3.title', '成本控制'),
          description: t('ai.revenue.models.model1.feature3.description', '考虑电价波动最小化成本'),
        },
      ],
    },
    {
      title: t('ai.revenue.models.model2.title', '多币种平衡挖矿模型'),
      subtitle: t('ai.revenue.models.model2.subtitle', '分散风险，平衡收益的智能挖矿策略'),
      description: t('ai.revenue.models.model2.description', '该模型通过分析多种加密货币的价格相关性、挖矿难度和市场流动性，制定最优的币种分配策略，在不同币种间动态分配挖矿资源，降低单一币种价格波动带来的风险，保持稳定收益。'),
      features: [
        {
          icon: "🔄",
          title: t('ai.revenue.models.model2.feature1.title', '智能切换'),
          description: t('ai.revenue.models.model2.feature1.description', '自动在币种间切换算力'),
        },
        {
          icon: "📊",
          title: t('ai.revenue.models.model2.feature2.title', '风险评估'),
          description: t('ai.revenue.models.model2.feature2.description', '分析币种相关性降低风险'),
        },
        {
          icon: "📈",
          title: t('ai.revenue.models.model2.feature3.title', '收益预测'),
          description: t('ai.revenue.models.model2.feature3.description', '预测不同分配方案的回报'),
        },
      ],
    },
  ];
  
  // 案例分析数据
  const caseStudies = [
    {
      title: t('ai.revenue.cases.case1.title', 'BTC大型矿场收益优化'),
      tag: t('ai.revenue.cases.case1.tag', '大型矿场'),
      description: t('ai.revenue.cases.case1.description', '某拥有5000台ASIC矿机的大型比特币矿场，通过部署BestMine AI系统，实现了挖矿参数的动态优化和设备健康监控，在保持相同算力的情况下显著降低了能耗和维护成本。'),
      results: [
        { value: '32%', label: t('ai.revenue.cases.case1.result1', '能耗降低') },
        { value: '28%', label: t('ai.revenue.cases.case1.result2', '维护成本减少') },
        { value: '18%', label: t('ai.revenue.cases.case1.result3', '净收益增加') },
      ],
    },
    {
      title: t('ai.revenue.cases.case2.title', '多币种矿场风险管理'),
      tag: t('ai.revenue.cases.case2.tag', '风险管理'),
      description: t('ai.revenue.cases.case2.description', '某支持多种币种挖矿的中型矿场，通过部署BestMine AI的多币种平衡挖矿模型，在市场高度波动期间依然保持了稳定收益，避免了单一币种价格下跌带来的重大损失。'),
      results: [
        { value: '45%', label: t('ai.revenue.cases.case2.result1', '风险降低') },
        { value: '23%', label: t('ai.revenue.cases.case2.result2', '波动降低') },
        { value: '15%', label: t('ai.revenue.cases.case2.result3', '长期收益提升') },
      ],
    },
  ];
  
  return (
    <RevenueContainer>
      <AdvantageSection>
        <AnimatedSection direction="up" delay={0.1}>
          <h2>{t('ai.revenue.advantages.title', 'AI 挖矿收益优势')}</h2>
          <p className="section-intro">
            {t('ai.revenue.advantages.intro', 'BITENGINE AI 通过先进的算法和智能优化，为矿工提供全方位的收益提升方案，从市场分析到设备维护，实现挖矿收益最大化。')}
          </p>
          
          <AdvantageGrid>
            {advantages.map((advantage, index) => (
              <AdvantageCard key={index}>
                <div className="card-header">
                  <div className="icon">{advantage.icon}</div>
                  <h3>{advantage.title}</h3>
                </div>
                <p>{advantage.description}</p>
                <div className="metrics">
                  {advantage.metrics.map((metric, i) => (
                    <div className="metric" key={i}>
                      <div className="value">{metric.value}</div>
                      <div className="label">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </AdvantageCard>
            ))}
          </AdvantageGrid>
        </AnimatedSection>
      </AdvantageSection>
      
      <ModelSection>
        <AnimatedSection direction="up" delay={0.2}>
          <h2>{t('ai.revenue.models.title', '智能收益优化模型')}</h2>
          
          {revenueModels.map((model, index) => (
            <ModelCard key={index}>
              <div className="card-header">
                <h3>{model.title}</h3>
                <p>{model.subtitle}</p>
              </div>
              <div className="card-body">
                <p>{model.description}</p>
                <ModelFeatures>
                  {model.features.map((feature, i) => (
                    <ModelFeature key={i}>
                      <div className="icon">{feature.icon}</div>
                      <div>
                        <h4>{feature.title}</h4>
                        <p>{feature.description}</p>
                      </div>
                    </ModelFeature>
                  ))}
                </ModelFeatures>
              </div>
            </ModelCard>
          ))}
        </AnimatedSection>
      </ModelSection>
      
      <CaseStudySection>
        <AnimatedSection direction="up" delay={0.3}>
          <h2>{t('ai.revenue.cases.title', '收益优化案例分析')}</h2>
          
          <CaseStudyCards>
            {caseStudies.map((caseStudy, index) => (
              <CaseStudyCard key={index}>
                <h3>
                  {caseStudy.title}
                  <span className="tag">{caseStudy.tag}</span>
                </h3>
                <p>{caseStudy.description}</p>
                <div className="results">
                  {caseStudy.results.map((result, i) => (
                    <div className="result" key={i}>
                      <div className="value">{result.value}</div>
                      <div className="label">{result.label}</div>
                    </div>
                  ))}
                </div>
              </CaseStudyCard>
            ))}
          </CaseStudyCards>
        </AnimatedSection>
      </CaseStudySection>
    </RevenueContainer>
  );
};

export default AiRevenue; 